import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { fetchActiveDashboardMessages } from "../data/DashboardNotifications/dashboardMessagesApi";

const POLL_INTERVAL_MS = 60000;
const DISMISSED_STORAGE_KEY = "dashboardMessages.dismissedIds";
const SEEN_MODAL_STORAGE_KEY = "dashboardMessages.seenModalIds";

const readIdSet = (key, storage) => {
  try {
    const raw = storage.getItem(key);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
};

const DashboardNotificationsContext = createContext(null);

// Fetches active dashboard messages (banners/modals/cards/toasts) on an
// interval, and derives the per-display-type, priority-ordered lists that
// the various notification components render from. There is no backend
// "dismiss" endpoint in the current API contract (only create/update/query),
// so dismissal is tracked purely client-side.
export const DashboardNotificationsProvider = ({ children }) => {
  const [messages, setMessages] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(() =>
    readIdSet(DISMISSED_STORAGE_KEY, window.localStorage)
  );
  const [seenModalIds, setSeenModalIds] = useState(() =>
    readIdSet(SEEN_MODAL_STORAGE_KEY, window.sessionStorage)
  );
  const [activeModalId, setActiveModalId] = useState(null);

  const fetchMessages = useCallback(async () => {
    try {
      const results = await fetchActiveDashboardMessages();
      setMessages(Array.isArray(results) ? results : []);
    } catch (error) {
      console.error("Failed to fetch dashboard messages:", error);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  const visibleMessages = useMemo(
    () => messages.filter((m) => !dismissedIds.has(String(m.id))),
    [messages, dismissedIds]
  );

  const banners = useMemo(
    () =>
      visibleMessages
        .filter((m) => m.displayType === "banner")
        .sort((a, b) => b.priority - a.priority),
    [visibleMessages]
  );

  const toasts = useMemo(
    () =>
      visibleMessages
        .filter((m) => m.displayType === "toast")
        .sort((a, b) => b.priority - a.priority),
    [visibleMessages]
  );

  const cards = useMemo(
    () =>
      visibleMessages
        .filter((m) => m.displayType === "card")
        .sort(
          (a, b) =>
            b.priority - a.priority ||
            new Date(b.createdAt) - new Date(a.createdAt)
        ),
    [visibleMessages]
  );

  const modals = useMemo(
    () =>
      visibleMessages
        .filter((m) => m.displayType === "modal")
        .sort((a, b) => b.priority - a.priority),
    [visibleMessages]
  );

  // Only one modal at a time, and only once per session per message id.
  useEffect(() => {
    if (activeModalId !== null) return;
    const nextModal = modals.find((m) => !seenModalIds.has(String(m.id)));
    if (nextModal) setActiveModalId(nextModal.id);
  }, [modals, seenModalIds, activeModalId]);

  const activeModal = useMemo(
    () => modals.find((m) => m.id === activeModalId) || null,
    [modals, activeModalId]
  );

  const dismiss = useCallback((id) => {
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(String(id));
      window.localStorage.setItem(
        DISMISSED_STORAGE_KEY,
        JSON.stringify(Array.from(next))
      );
      return next;
    });
  }, []);

  const closeModal = useCallback(
    (id, { dismissForever } = {}) => {
      setSeenModalIds((prev) => {
        const next = new Set(prev);
        next.add(String(id));
        window.sessionStorage.setItem(
          SEEN_MODAL_STORAGE_KEY,
          JSON.stringify(Array.from(next))
        );
        return next;
      });
      setActiveModalId(null);
      if (dismissForever) dismiss(id);
    },
    [dismiss]
  );

  const value = useMemo(
    () => ({
      banners,
      toasts,
      cards,
      activeModal,
      dismiss,
      closeModal,
      unreadCardCount: cards.length,
      refetch: fetchMessages,
    }),
    [banners, toasts, cards, activeModal, dismiss, closeModal, fetchMessages]
  );

  return (
    <DashboardNotificationsContext.Provider value={value}>
      {children}
    </DashboardNotificationsContext.Provider>
  );
};

export const useDashboardNotifications = () => {
  const ctx = useContext(DashboardNotificationsContext);
  if (!ctx) {
    throw new Error(
      "useDashboardNotifications must be used within a DashboardNotificationsProvider"
    );
  }
  return ctx;
};
